import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { TopItem } from "@/components/MobileDrawerItem";

/**
 * Animated mobile drawer used by both PublicShell and AppShell.
 * Supports nested item groups (see MobileDrawerItem.jsx).
 */
export default function MobileMenuDrawer({ open, onClose, user, items = [], footer, brand }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const lastFocused = document.activeElement;
    const focusFirst = () => {
      const target = document.querySelector('[data-testid="mobile-drawer-close"]');
      if (target instanceof HTMLElement) target.focus();
    };
    const id = setTimeout(focusFirst, 30);
    const onTab = (e) => {
      if (e.key !== "Tab") return;
      const dialog = document.querySelector('[data-testid="mobile-drawer"] aside');
      if (!dialog) return;
      const focusables = dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onTab);
    return () => {
      clearTimeout(id);
      document.removeEventListener("keydown", onTab);
      if (lastFocused instanceof HTMLElement) lastFocused.focus();
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] md:hidden" data-testid="mobile-drawer">
      <button type="button" aria-label="Close menu" onClick={onClose}
        className="kpp-drawer-overlay absolute inset-0 bg-ink/40 backdrop-blur-sm" />
      <aside className="kpp-drawer-panel absolute top-0 right-0 h-full w-[84%] max-w-[340px] bg-cream shadow-soft-lg rounded-l-[2rem] flex flex-col overflow-hidden"
        role="dialog" aria-modal="true">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          onPointerDown={(e) => e.stopPropagation()}
          aria-label="Close menu"
          className="absolute top-3 right-3 z-20 w-9 h-9 rounded-full flex items-center justify-center text-inkmid bg-cream hover:bg-plum/10 active:scale-95 transition shadow-soft-sm"
          data-testid="mobile-drawer-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="kpp-drawer-item px-5 sm:px-6 pt-7 pb-5" style={{ animationDelay: "60ms" }}>
          {user ? (
            <Link to={user.profileLink || "/edit-profile"} onClick={onClose} className="flex items-center gap-3.5">
              <div className="w-14 h-14 rounded-full overflow-hidden shrink-0 ring-2 ring-coral/20 bg-gradient-to-br from-coral/20 to-mint/20 flex items-center justify-center text-ink font-heading text-xl font-bold">
                {user.avatar ? <img src={user.avatar} alt={user.name || ""} className="w-full h-full object-cover" /> : <span>{(user.name || "K").trim().charAt(0).toUpperCase()}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-heading text-lg text-ink leading-tight truncate">{user.name || "Welcome"}</p>
                  {user.planBadge && (
                    <span className={"text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 " + (user.planBadge === "PRO" ? "bg-coral text-white" : "bg-plum/10 text-plum")}>{user.planBadge}</span>
                  )}
                </div>
                {user.accountType && (
                  <p
                    className="text-[10px] font-bold uppercase tracking-[0.14em] text-coral mt-0.5"
                    data-testid="drawer-account-type"
                  >
                    {user.accountType} account
                  </p>
                )}
                {user.email && <p className="text-[11px] text-inkmid truncate mt-0.5" data-testid="drawer-user-email">{user.email}</p>}
                {user.subtitle && !user.email && <p className="text-xs text-inkmid truncate mt-0.5">{user.subtitle}</p>}
              </div>
            </Link>
          ) : (
            <Link to="/" onClick={onClose} className="flex items-center gap-3">
              {brand?.logo && <img src={brand.logo} alt="" className="w-12 h-12 rounded-[22%] shadow-soft-sm" />}
              {brand?.wordmark && <img src={brand.wordmark} alt="Kiddo+" className="h-7 w-auto" />}
            </Link>
          )}
        </div>

        <div className="mx-5 sm:mx-6 h-px bg-plum/10" />

        <nav className="flex-1 overflow-y-auto px-3 sm:px-4 py-4">
          <ul className="space-y-1.5">
            {items.map((it, idx) => (
              <li key={(it.to || it.href || it.label) + "-" + idx} className="kpp-drawer-item" style={{ animationDelay: (120 + idx * 55) + "ms" }}>
                <TopItem item={it} onClose={onClose} />
              </li>
            ))}
          </ul>
        </nav>

        {footer && (
          <div className="kpp-drawer-item border-t border-plum/10 px-5 sm:px-6 pt-4 pb-7" style={{ animationDelay: (120 + items.length * 55 + 60) + "ms" }}>
            {footer}
          </div>
        )}
      </aside>
    </div>
  );
}
